import { z } from 'zod';
export * from './overview-metrics.schema.js';
import { CustomerStatusSchema, CustomerTypeSchema, OrganizationSizeSchema, OrganizationStatusSchema, ProfileKindSchema, StringFilterOperatorSchema, } from '../common/enums.js';
import { MetadataSchema, PaginationQuerySchema } from '../common/types.js';
export const OrganizationSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    name: z.string(),
    legal_name: z.string().nullable(),
    external_id: z.string().nullable(),
    description: z.string().nullable(),
    website: z.string().nullable(),
    industry: z.string().nullable(),
    size: OrganizationSizeSchema.nullable(),
    employee_count: z.number().int().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address_line1: z.string().nullable(),
    address_line2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    tax_id: z.string().nullable(),
    currency: z.string(),
    timezone: z.string(),
    default_payment_terms: z.number().int(),
    status: OrganizationStatusSchema,
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const OrganizationSummarySchema = z.object({
    id: z.string().optional(),
    name: z.string(),
});
export const CustomerSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    organization_id: z.string().nullable(),
    type: CustomerTypeSchema,
    profile_kind: ProfileKindSchema.nullable(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    external_id: z.string().nullable(),
    agent_name: z.string().nullable(),
    address_line1: z.string().nullable(),
    address_line2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string().nullable(),
    timezone: z.string().nullable(),
    currency: z.string().nullable(),
    tax_exempt: z.boolean(),
    tax_exemption_certificate: z.string().nullable(),
    tax_exemption_reason: z.string().nullable(),
    status: CustomerStatusSchema,
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CustomerTaxSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    customer_id: z.string(),
    tax_id: z.string(),
    priority: z.number().int(),
    is_active: z.boolean(),
    currency: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreateOrganizationSchema = z.object({
    name: z.string().min(1).max(255),
    legal_name: z.string().max(255).optional(),
    external_id: z.string().max(255).optional(),
    description: z.string().optional(),
    website: z.string().url().optional(),
    industry: z.string().optional(),
    size: OrganizationSizeSchema.optional(),
    employee_count: z.number().int().positive().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    address_line1: z.string().optional(),
    address_line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    country: z.string().optional(),
    tax_id: z.string().optional(),
    currency: z.string().default('USD'),
    timezone: z.string().default('UTC'),
    default_payment_terms: z.number().int().default(30),
    status: OrganizationStatusSchema.default('active'),
    metadata: MetadataSchema.default({}),
});
export const CreateCustomerSchema = z.object({
    organization_id: z.string().nullable().optional(),
    type: CustomerTypeSchema,
    profile_kind: ProfileKindSchema.optional(),
    name: z.string().min(1).max(255),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    external_id: z.string().max(255).optional(),
    agent_name: z.string().max(255).optional(),
    address_line1: z.string().optional(),
    address_line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    country: z.string().optional(),
    timezone: z.string().optional(),
    currency: z.string().optional(),
    tax_exempt: z.boolean().default(false),
    tax_exemption_certificate: z.string().optional(),
    tax_exemption_reason: z.string().optional(),
    status: CustomerStatusSchema.default('active'),
    metadata: MetadataSchema.default({}),
});
export const CreateCustomerTaxSchema = z.object({
    tax_id: z.string(),
    priority: z.number().int().positive().default(1),
    is_active: z.boolean().default(true),
    currency: z.string().length(3).default('USD'),
});
export const UpdateOrganizationSchema = CreateOrganizationSchema.partial();
export const UpdateCustomerSchema = CreateCustomerSchema.partial();
export const UpdateCustomerTaxSchema = CreateCustomerTaxSchema.partial();
export const OrganizationQuerySchema = PaginationQuerySchema.extend({
    status: OrganizationStatusSchema.optional(),
    search: z.string().optional(),
    external_id: z.string().optional(),
});
export const CustomerQuerySchema = PaginationQuerySchema.extend({
    organization_id: z.string().optional(),
    type: CustomerTypeSchema.optional(),
    profile_kind: ProfileKindSchema.optional(),
    status: CustomerStatusSchema.optional(),
    search: z.string().optional(),
    external_id: z.string().optional(),
    filter_field: z
        .enum(['name', 'email', 'external_id', 'billing_id', 'organization_name'])
        .optional(),
    filter_operator: StringFilterOperatorSchema.optional(),
    filter_value: z.string().optional(),
});
export const OrganizationStatsSchema = z.object({
    customers_count: z.number(),
    agents_count: z.number(),
    active_contracts_count: z.number(),
    monthly_revenue: z.number(),
});
export const OrganizationResponseSchema = OrganizationSchema.extend({
    stats: OrganizationStatsSchema.optional(),
});
export const CustomerStatsSchema = z.object({
    active_contracts: z.number(),
    total_contracts: z.number(),
    total_invoices: z.number(),
    total_revenue: z.number(),
    lifetime_value: z.number(),
});
export const CustomerResponseSchema = CustomerSchema.extend({
    organization: OrganizationSummarySchema.optional(),
    taxes: z.array(CustomerTaxSchema).optional(),
    stats: CustomerStatsSchema.optional(),
});
export const PaginatedOrganizationsResponseSchema = z.object({
    data: z.array(OrganizationResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const PaginatedCustomersResponseSchema = z.object({
    data: z.array(CustomerResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const OrganizationParamsSchema = z.object({
    id: z.string(),
});
export const CustomerParamsSchema = z.object({
    id: z.string(),
});
export const CustomerTaxParamsSchema = z.object({
    customerId: z.string(),
    taxId: z.string(),
});
export const OrganizationTaxParamsSchema = z.object({
    id: z.string(),
    taxId: z.string(),
});
export const DeleteOrganizationResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});
//# sourceMappingURL=index.js.map